<template>
  <div>
    <v-row dense class="mb-3">
      <v-col cols="3">
        <v-select
          v-model="form.apartment.building.building_complex.id"
          item-text="name"
          item-value="id"
          :items="buildingComplexesList"
          :readonly="readonly"
          :rules="rules.requiredSelectNotArray"
          :label="`${$t('BUILDING_COMPLEXES.ITEM')} *`"
        ></v-select>
      </v-col>
      <v-col cols="3">
        <v-select
          v-model="form.apartment.building.id"
          :item-text="i => [i.street, i.name].filter(Boolean).join(' — ')"
          item-value="id"
          :items="buildings"
          :readonly="readonly"
          :rules="rules.requiredSelectNotArray"
          :label="`${$t('BUILDINGS.ITEM')} *`"
          :hint="hasNotrufMessage"
          persistent-hint
        />
      </v-col>
      <v-col cols="3">
        <v-select
          v-model="form.apartment_id"
          item-text="name"
          item-value="id"
          :items="apartments"
          :readonly="readonly"
          :rules="rules.requiredSelectNotArray"
          :label="`${$t('APARTMENTS.ITEM')} *`"
          :hint="`${$t('APARTMENTS.ROOMS')}: ${apartmentsObject().rooms || ''}`"
          persistent-hint
          :error-messages="errors.apartment_id"
          @change="updateValue('apartment_id', $event)"
        />
      </v-col>
      <v-col cols="3">
        <v-text-field
          v-if="readonly"
          :value="form.date_from | dateFormat"
          :label="$t('INHABITANTS.DATE_FROM')"
          append-icon="mdi-calendar"
          readonly
        ></v-text-field>
        <v-menu
          v-else
          ref="menuDateFrom"
          v-model="menuDateFrom"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="290px"
        >
          <template v-slot:activator="{ on }">
            <v-text-field
              :value="form.date_from | dateFormat"
              :label="`${$t('INHABITANTS.DATE_FROM')} *`"
              append-icon="mdi-calendar"
              clearable
              readonly
              role="button"
              :rules="rules.requiredTextLine"
              autocomplete="nope"
              :error-messages="errors.date_from"
              @click:clear="clearFrom"
              v-on="on"
              @change="updateValue('date_from', $event)"
            ></v-text-field>
          </template>
          <v-date-picker
            ref="picker"
            v-model="form.date_from"
            min="1940-01-01"
            first-day-of-week="1"
            @change="saveDateFrom"
          ></v-date-picker>
        </v-menu>
      </v-col>
    </v-row>
  </div>
</template>
<script>
import formEditable from '@/modules/_global/mixins/form-editable';
import {
  requiredSelectNotArray,
  requiredTextLine
} from '@/modules/_global/variables/rules.js';
import { mapCacheActions } from 'vuex-cache';
import { mapActions, mapState } from 'vuex';
import { dateFormat } from '@/modules/_global/filters';
import { debounce, isEqual, isEmpty, cloneDeep } from 'lodash';

const DEFAULT_FORM = {
  apartment_id: null,
  apartment: {
    building: {
      id: null,
      has_notruf: false,
      building_complex: {
        management_company: { name: null },
        business_customer: { name: null },
        concierge: { person: null }
      }
    }
  }
};

export default {
  name: 'InhabitantForm',

  filters: {
    dateFormat
  },

  mixins: [formEditable],

  props: {
    delay: {
      type: Number,
      default: 300
    }
  },

  data() {
    return {
      form: cloneDeep(DEFAULT_FORM),
      buildings: [],
      apartments: [],
      menuDateFrom: null,
      rules: {
        requiredTextLine,
        requiredSelectNotArray
      },
      getDataBuildingsDebounced: null,
      getDataApartmentsDebounced: null
    };
  },

  computed: {
    ...mapState('building_complex', ['buildingComplexesList']),

    hasNotrufMessage() {
      return this.form.apartment.building.has_notruf
        ? `* ${this.$t('BUILDINGS.NOTRUF_INCLUDED')}`
        : '';
    }
  },

  watch: {
    'form.apartment.building.building_complex.id':
      'changeSelectedBuildingComplex',
    'form.apartment.building.id': 'changeSelectedBuilding'
  },

  created() {
    this.setData();
    this.getDataBuildingsDebounced = debounce(
      this.getDataBuildings,
      this.delay
    );
    this.getDataApartmentsDebounced = debounce(
      this.getDataApartments,
      this.delay
    );
  },

  methods: {
    ...mapCacheActions('building_complex', ['getBuildingComplexesList']),
    ...mapActions('building_complex', [
      'getCheckinBuildingComplex',
      'getApartments'
    ]),

    apartmentsObject() {
      if (this.form.apartment.building.apartments && this.form.apartment_id) {
        return (
          this.form.apartment.building.apartments.find(
            el => el.id === this.form.apartment_id
          ) || {}
        );
      } else {
        return this.form.apartment;
      }
    },

    valueWatchHandler(formProps) {
      if (isEqual(formProps, this.form)) {
        return;
      }

      if (isEmpty(formProps)) {
        this.form = cloneDeep(DEFAULT_FORM);
      } else {
        this.form = {
          ...this.form,
          ...formProps
        };
      }

      this.emitter();
    },

    setData() {
      this.getDataBuildingComplexes()
        .then(() => {
          return this.getDataBuildings();
        })
        .then(() => {
          return this.getDataApartments();
        });
    },

    getDataBuildingComplexes() {
      return this.getBuildingComplexesList({
        params: { statuses: ['active'] }
      }).catch(error => {
        this.error = error;
      });
    },

    getDataBuildings(
      buildingComplexId = this.form.apartment.building.building_complex.id
    ) {
      if (buildingComplexId) {
        return this.getCheckinBuildingComplex(buildingComplexId).then(
          ({ data }) => {
            let { buildings, ...buildingComplex } = data;
            this.buildings = buildings;
            this.form.apartment.building.building_complex = buildingComplex;
            return data;
          }
        );
      }
    },

    getDataApartments(buildingId = this.form.apartment.building.id) {
      if (buildingId) {
        return this.getApartments(buildingId).then(data => {
          this.apartments = data.data.data;
          return data;
        });
      }
    },

    changeSelectedBuildingComplex(id) {
      this.form.apartment.building.id = null;

      if (id !== this.value.apartment.building.building_complex.id) {
        this.form.apartment_id = null;
        this.buildings = [];
        this.form.apartment = {
          building: {
            apartments: null,
            has_notruf: null,
            id: null,
            building_complex: {
              id: id,
              management_company: { name: null },
              business_customer: { name: null },
              concierge: { person: null }
            }
          }
        };
      }

      this.form.apartment.building.has_notruf = null;
      this.form.apartment_id = null;
      this.apartments = [];

      return this.getDataBuildingsDebounced();
    },

    changeSelectedBuilding(id) {
      const selectedBuilding =
        this.buildings.filter(item => item.id === id).pop() || {};
      this.form.apartment.building.id = id;
      this.form.apartment.building.street = selectedBuilding.street;
      this.form.apartment.building.apartments = selectedBuilding.apartments;
      this.form.apartment.building.has_notruf = selectedBuilding.has_notruf;
      this.form.apartment_id = null;

      return this.getDataApartmentsDebounced();
    },

    clearFrom() {
      this.form.date_from = null;
    },

    saveDateFrom(date) {
      this.$refs.menuDateFrom.save(date);
    }
  }
};
</script>
